import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { Slider } from '@/components/ui/slider';
import { Lightbulb, TrendingUp, RefreshCw, Hammer, DollarSign, Plus, X, Building2, Zap, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const inputCls = "w-full bg-white border border-[#E2DDD3] rounded-lg px-3 py-2.5 text-[13px] text-[#1C1C1C] font-sans placeholder:text-[#D9D3C8] focus:border-[#1F3A34] focus:ring-1 focus:ring-[#1F3A34]/20 focus:outline-none transition-all";
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';

function mp(p, r, y) { if (r === 0) return y > 0 ? p / (y * 12) : 0; const rm = r / 12, n = y * 12; return p * (rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1); }

function Delta({ value, suffix = '' }) {
  if (!value || Math.abs(value) < 0.01) return null;
  const pos = value > 0;
  return (
    <span className={`flex items-center gap-0.5 text-[11px] font-sans font-semibold ${pos ? 'text-[#2E6F57]' : 'text-[#8A3C3C]'}`}>
      {pos ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {pos ? '+' : ''}{typeof value === 'number' && Math.abs(value) >= 100 ? fmt(value) : value.toFixed(2)}{suffix}
    </span>
  );
}

function SimMetric({ label, base, sim, suffix = '', invert = false }) {
  const delta = sim - base;
  const improved = invert ? delta < 0 : delta > 0;
  return (
    <div className="space-y-0.5">
      <p className="text-[10px] text-[#8C877F] font-sans">{label}</p>
      <p className="text-[16px] font-sans font-semibold text-[#1C1C1C]">{typeof sim === 'number' && Math.abs(sim) < 1000 ? sim.toFixed(2) : fmt(sim)}{suffix}</p>
      {Math.abs(delta) > 0.005 && <p className={`text-[10px] font-sans font-medium ${improved ? 'text-[#2E6F57]' : 'text-[#8A3C3C]'}`}>{delta > 0 ? '+' : ''}{Math.abs(delta) >= 100 ? fmt(delta) : delta.toFixed(2)}{suffix}</p>}
    </div>
  );
}

export default function Opportunities() {
  const { assets: ctxAssets, loading: pfLoading } = usePortfolio();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAsset, setExpandedAsset] = useState(null);
  const [showProspect, setShowProspect] = useState(false);

  // Per-asset sim state
  const [sims, setSims] = useState({});

  // Prospect form
  const [pName, setPName] = useState(''); const [pCity, setPCity] = useState(''); const [pPrice, setPPrice] = useState('');
  const [pRent, setPRent] = useState(''); const [pCosts, setPCosts] = useState(''); const [pFin, setPFin] = useState('70'); const [pRate, setPRate] = useState('3.5');

  useEffect(() => {
    if (pfLoading) return;
    setAssets(ctxAssets);
    (async () => {
      try {
        // Prospects from localStorage
      } catch (e) { console.error(e); } finally { setLoading(false); }
    })();
  }, []);

  const getSim = (id) => sims[id] || { priceDelta: 0, rentDelta: 0, renoInvest: 0, renoRentUp: 5, renoValueUp: 3 };
  const setSim = (id, key, val) => setSims(prev => ({ ...prev, [id]: { ...getSim(id), [key]: val } }));

  // Compute enriched with sim
  const enriched = useMemo(() => {
    return assets.map(a => {
      const m = a.metrics || {};
      const sim = getSim(a.asset_id);
      const baseMv = m.market_value || 0, baseRent = a.annual_rent || 0, la = m.loan_amount || 0, ir = m.interest_rate || 0;
      const baseNoi = m.noi || 0, baseAds = m.annual_debt_service || 0, basePp = a.purchase_price || 0;

      // Simulated values
      const simPp = basePp * (1 + sim.priceDelta / 100);
      const simRent = baseRent * (1 + sim.rentDelta / 100);
      const renoValue = sim.renoInvest > 0 ? baseMv * (1 + sim.renoValueUp / 100) : baseMv;
      const renoRent = sim.renoInvest > 0 ? simRent * (1 + sim.renoRentUp / 100) : simRent;
      const simMv = renoValue;
      const simNoi = renoRent * (1 - (a.vacancy_rate || 0) / 100) - (a.operating_costs || 0);
      const simLtv = simMv > 0 ? la / simMv * 100 : 0;
      const simDscr = baseAds > 0 ? simNoi / baseAds : 0;
      const simEi = Math.max(simPp - la, simPp) + (sim.renoInvest || 0);
      const simCoc = simEi > 0 ? (simNoi - baseAds) / simEi * 100 : 0;
      const simDy = la > 0 ? simNoi / la * 100 : 0;
      const simGrossYield = simPp > 0 ? renoRent / simPp * 100 : 0;
      const simNetYield = simMv > 0 ? simNoi / simMv * 100 : 0;
      const simNcf = simNoi - baseAds;

      // IRR (simplified 5yr)
      const init = -simEi;
      const cfs = [init, ...Array(4).fill(simNcf), simNcf + simMv - la];
      let irrVal = 0.1;
      for (let iter = 0; iter < 100; iter++) {
        const npv = cfs.reduce((s, cf, i) => s + cf / Math.pow(1 + irrVal, i), 0);
        const dnpv = cfs.reduce((s, cf, i) => s + -i * cf / Math.pow(1 + irrVal, i + 1), 0);
        if (Math.abs(dnpv) < 1e-10) break;
        irrVal -= npv / dnpv;
        if (Math.abs(npv) < 1) break;
      }
      const simIrr = (isFinite(irrVal) ? irrVal : 0) * 100;

      // Opportunity scores
      const mktRentEst = baseRent * 1.12;
      const rentGapPct = baseRent > 0 ? (mktRentEst - baseRent) / baseRent * 100 : 0;
      const refiSpread = ir - 3.0;
      const rentGapScore = Math.min(30, Math.round(rentGapPct * 1.5));
      const refiScore = Math.min(25, Math.round(refiSpread * 12));
      const valueAddScore = Math.min(20, sim.renoInvest > 0 ? 15 : (m.net_yield || 0) < 4 ? 12 : 5);
      const pricingScore = Math.min(15, Math.round(Math.max(0, sim.priceDelta) * -0.5 + 8));
      const riskAdjScore = Math.min(10, Math.round((simIrr > 12 ? 8 : simIrr > 8 ? 5 : 2)));
      const oppScore = rentGapScore + refiScore + valueAddScore + pricingScore + riskAdjScore;
      const maxScore = 30 + 25 + 20 + 15 + 10;

      // Negotiation range
      const targetIrr = 0.10;
      const conservOffer = baseMv * 0.88;
      const targetOffer = baseMv * 0.92;
      const aggressiveOffer = baseMv * 0.95;
      const walkAway = baseMv * 1.02;

      // Payback for renovation
      const renoPayback = sim.renoInvest > 0 && (renoRent - simRent) > 0 ? sim.renoInvest / ((renoRent - simRent) * (1 - (a.vacancy_rate || 0) / 100)) : 0;

      return {
        ...a, sim, simPp, simRent, simMv, simNoi, simLtv, simDscr, simIrr, simCoc, simDy, simGrossYield, simNetYield, simNcf,
        oppScore, maxScore, rentGapScore, refiScore, valueAddScore, pricingScore, riskAdjScore,
        rentGapPct, refiSpread, renoPayback,
        conservOffer, targetOffer, aggressiveOffer, walkAway,
        baseLtv: m.ltv || 0, baseDscr: m.dscr || 0, baseIrr: m.irr || 0, baseNcf: m.net_cashflow || 0,
      };
    }).sort((a, b) => b.oppScore - a.oppScore);
  }, [assets, sims]);

  // Portfolio deltas
  const portfolioDelta = useMemo(() => {
    const baseCf = assets.reduce((s, a) => s + (a.metrics?.net_cashflow || 0), 0);
    const simCf = enriched.reduce((s, a) => s + a.simNcf, 0);
    return { cfDelta: simCf - baseCf };
  }, [assets, enriched]);

  // Prospects
  const [prospects, setProspects] = useState(() => { try { return JSON.parse(localStorage.getItem('loeq_prospects') || '[]'); } catch { return []; } });
  const addProspect = () => {
    const price = parseFloat(pPrice) || 0, rent = parseFloat(pRent) || 0, costs = parseFloat(pCosts) || 0;
    const fin = parseFloat(pFin) || 70, rate = parseFloat(pRate) || 3.5, la = price * fin / 100;
    const noi = rent - costs, ads = mp(la, rate / 100, 30) * 12, ncf = noi - ads;
    const gy = price > 0 ? rent / price * 100 : 0, ny = price > 0 ? noi / price * 100 : 0;
    const dscr = ads > 0 ? noi / ads : 0, ltv = fin;
    const p = { id: Date.now().toString(), name: pName, city: pCity, price, rent, costs, fin, rate, la, noi, ncf, gy, ny, dscr, ltv };
    const upd = [...prospects, p]; setProspects(upd); localStorage.setItem('loeq_prospects', JSON.stringify(upd));
    setShowProspect(false); setPName(''); setPCity(''); setPPrice(''); setPRent(''); setPCosts('');
  };
  const removeProspect = (id) => { const upd = prospects.filter(p => p.id !== id); setProspects(upd); localStorage.setItem('loeq_prospects', JSON.stringify(upd)); };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="opportunities-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Asset Opportunity & Pricing Lab</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Deal Structuring</h1>
        </div>
        <button onClick={() => setShowProspect(!showProspect)}
          className="flex items-center gap-1.5 px-4 py-2.5 bg-[#1F3A34] text-white rounded-lg text-[13px] font-sans font-medium hover:bg-[#285047] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add Prospect
        </button>
      </div>

      {/* Portfolio Impact */}
      {Math.abs(portfolioDelta.cfDelta) > 10 && (
        <div className={`rounded-xl p-4 flex items-center gap-4 ${portfolioDelta.cfDelta >= 0 ? 'bg-[#E6EFEA] border border-[#1F3A34]/10' : 'bg-[#FDF5F5] border border-[#8A3C3C]/15'}`}>
          <Zap className="w-4 h-4" style={{ color: portfolioDelta.cfDelta >= 0 ? '#1F3A34' : '#8A3C3C' }} />
          <p className="text-[13px] font-sans font-medium" style={{ color: portfolioDelta.cfDelta >= 0 ? '#1F3A34' : '#8A3C3C' }}>
            Simulation Impact: {portfolioDelta.cfDelta >= 0 ? '+' : ''}{fmt(portfolioDelta.cfDelta)}/yr portfolio cashflow
          </p>
        </div>
      )}

      {/* Prospect Form */}
      {showProspect && (
        <div className="bg-white border border-[#1F3A34]/20 rounded-xl p-6 space-y-4 animate-fade-in" style={{ boxShadow: '0 4px 16px rgba(31,58,52,0.08)' }}>
          <div className="flex items-center justify-between"><p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">New Acquisition Prospect</p><button onClick={() => setShowProspect(false)} className="text-[#8C877F] hover:text-[#1C1C1C]"><X className="w-4 h-4" /></button></div>
          <div className="grid grid-cols-5 gap-4">
            <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">Name *</label><input className={inputCls} value={pName} onChange={e => setPName(e.target.value)} placeholder="Damrak 45" /></div>
            <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">City *</label><input className={inputCls} value={pCity} onChange={e => setPCity(e.target.value)} placeholder="Amsterdam" /></div>
            <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">Price (EUR) *</label><input type="number" className={inputCls} value={pPrice} onChange={e => setPPrice(e.target.value)} placeholder="920000" /></div>
            <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">Annual Rent *</label><input type="number" className={inputCls} value={pRent} onChange={e => setPRent(e.target.value)} placeholder="52000" /></div>
            <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1 block">Costs / Fin% / Rate</label><div className="flex gap-1"><input type="number" className={inputCls + ' w-20'} value={pCosts} onChange={e => setPCosts(e.target.value)} placeholder="5k" /><input type="number" className={inputCls + ' w-14'} value={pFin} onChange={e => setPFin(e.target.value)} /><input type="number" className={inputCls + ' w-14'} value={pRate} onChange={e => setPRate(e.target.value)} /></div></div>
          </div>
          <button onClick={addProspect} disabled={!pName || !pPrice || !pRent} className="bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[13px] px-6 py-2.5 font-sans font-medium disabled:opacity-40">Calculate & Save</button>
        </div>
      )}

      {/* Prospects */}
      {prospects.length > 0 && (
        <div className="space-y-3">
          <p className="text-[12px] text-[#1F3A34] font-sans font-medium">Acquisition Prospects ({prospects.length})</p>
          {prospects.map(p => (
            <div key={p.id} className="bg-white border border-[#1F3A34]/15 rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(31,58,52,0.05)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3"><Building2 className="w-5 h-5 text-[#1F3A34]/50" /><div><p className="text-[15px] text-[#1C1C1C] font-sans font-semibold">{p.name}</p><p className="text-[11px] text-[#8C877F]">{p.city}</p></div></div>
                <button onClick={() => removeProspect(p.id)} className="text-[11px] text-[#8C877F] hover:text-[#8A3C3C] font-sans">Remove</button>
              </div>
              <div className="grid grid-cols-6 gap-4">
                {[{ l: 'Price', v: fmt(p.price) }, { l: 'Gross Yield', v: `${p.gy.toFixed(1)}%`, c: p.gy >= 5 ? '#2E6F57' : '#A67C52' }, { l: 'Net Yield', v: `${p.ny.toFixed(1)}%`, c: p.ny >= 4 ? '#2E6F57' : '#A67C52' }, { l: 'LTV', v: `${p.ltv}%` }, { l: 'Net CF/yr', v: fmt(p.ncf), c: p.ncf >= 0 ? '#2E6F57' : '#8A3C3C' }, { l: 'DSCR', v: p.dscr.toFixed(2), c: p.dscr >= 1.2 ? '#2E6F57' : '#8A3C3C' }].map(k => (
                  <div key={k.l}><p className="text-[10px] text-[#8C877F]">{k.l}</p><p className="text-[14px] font-sans font-semibold" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Asset Cards with Simulator */}
      <div className="space-y-4">
        <p className="text-[12px] text-[#8C877F] font-sans font-medium">Portfolio Assets — Click to simulate</p>
        {enriched.map(a => {
          const isExpanded = expandedAsset === a.asset_id;
          const sim = a.sim;
          const hasChanges = sim.priceDelta !== 0 || sim.rentDelta !== 0 || sim.renoInvest > 0;
          return (
            <div key={a.asset_id} className="bg-white border border-[#E2DDD3] rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              {/* Header row */}
              <div className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-[#F4F1EB]/40 transition-colors"
                onClick={() => setExpandedAsset(isExpanded ? null : a.asset_id)}>
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-[14px] font-sans font-bold ${a.oppScore >= 60 ? 'bg-[#E6EFEA] text-[#1F3A34]' : a.oppScore >= 35 ? 'bg-[#FDF8F0] text-[#A67C52]' : 'bg-[#F4F1EB] text-[#8C877F]'}`}>{a.oppScore}</div>
                  <div><p className="text-[15px] text-[#1C1C1C] font-sans font-semibold">{a.name}</p><p className="text-[11px] text-[#8C877F]">{a.city}</p></div>
                  {hasChanges && <span className="text-[10px] px-2 py-0.5 rounded-lg bg-[#E6EFEA] text-[#1F3A34] font-medium">Simulating</span>}
                </div>
                <div className="flex items-center gap-6">
                  {[{ l: 'IRR', v: `${(hasChanges ? a.simIrr : a.baseIrr).toFixed(1)}%`, c: '#1F3A34' }, { l: 'DSCR', v: (hasChanges ? a.simDscr : a.baseDscr).toFixed(2) }, { l: 'CF/yr', v: fmt(hasChanges ? a.simNcf : a.baseNcf), c: (hasChanges ? a.simNcf : a.baseNcf) >= 0 ? '#2E6F57' : '#8A3C3C' }].map(k => (
                    <div key={k.l} className="text-right hidden md:block"><p className="text-[9px] text-[#8C877F]">{k.l}</p><p className="text-[14px] font-sans font-semibold" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p></div>
                  ))}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-[#8C877F]" /> : <ChevronDown className="w-4 h-4 text-[#8C877F]" />}
                </div>
              </div>

              {/* Expanded Simulator */}
              {isExpanded && (
                <div className="px-5 pb-5 border-t border-[#E2DDD3] pt-5 space-y-6 animate-fade-in">
                  {/* Score Breakdown */}
                  <div className="grid grid-cols-5 gap-3">
                    {[{ l: 'Rent Gap', v: a.rentGapScore, max: 30 }, { l: 'Refi Spread', v: a.refiScore, max: 25 }, { l: 'Value-Add', v: a.valueAddScore, max: 20 }, { l: 'Pricing', v: a.pricingScore, max: 15 }, { l: 'Risk-Adj Return', v: a.riskAdjScore, max: 10 }].map(s => (
                      <div key={s.l} className="text-center">
                        <div className="w-full h-1.5 bg-[#F4F1EB] rounded-full mb-1"><div className="h-full rounded-full" style={{ width: `${s.v/s.max*100}%`, backgroundColor: s.v/s.max >= 0.6 ? '#2E6F57' : s.v/s.max >= 0.3 ? '#A67C52' : '#8C877F' }} /></div>
                        <p className="text-[10px] text-[#8C877F]">{s.l} <strong>{s.v}/{s.max}</strong></p>
                      </div>
                    ))}
                  </div>

                  {/* Sliders */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Price */}
                    <div className="space-y-3 bg-[#F4F1EB]/50 rounded-xl p-4">
                      <div className="flex justify-between"><p className="text-[12px] text-[#5A5A5A] font-sans font-medium">Purchase Price</p><span className="text-[13px] font-sans font-semibold text-[#1F3A34]">{sim.priceDelta > 0 ? '+' : ''}{sim.priceDelta}%</span></div>
                      <Slider value={[sim.priceDelta]} onValueChange={v => setSim(a.asset_id, 'priceDelta', v[0])} min={-20} max={10} step={1}
                        className="[&_[role=slider]]:bg-[#1F3A34] [&_[role=slider]]:border-0 [&_[role=slider]]:w-3 [&_[role=slider]]:h-3" />
                      <p className="text-[11px] text-[#8C877F]">{fmt(a.simPp)} (base {fmt(a.purchase_price || 0)})</p>
                    </div>
                    {/* Rent */}
                    <div className="space-y-3 bg-[#F4F1EB]/50 rounded-xl p-4">
                      <div className="flex justify-between"><p className="text-[12px] text-[#5A5A5A] font-sans font-medium">Rent Level</p><span className="text-[13px] font-sans font-semibold text-[#1F3A34]">{sim.rentDelta > 0 ? '+' : ''}{sim.rentDelta}%</span></div>
                      <Slider value={[sim.rentDelta]} onValueChange={v => setSim(a.asset_id, 'rentDelta', v[0])} min={-10} max={25} step={1}
                        className="[&_[role=slider]]:bg-[#1F3A34] [&_[role=slider]]:border-0 [&_[role=slider]]:w-3 [&_[role=slider]]:h-3" />
                      <p className="text-[11px] text-[#8C877F]">{fmt(a.simRent)}/yr (base {fmt(a.annual_rent || 0)})</p>
                    </div>
                    {/* Renovation */}
                    <div className="space-y-3 bg-[#F4F1EB]/50 rounded-xl p-4">
                      <p className="text-[12px] text-[#5A5A5A] font-sans font-medium">Renovation</p>
                      <div className="grid grid-cols-3 gap-2">
                        <div><label className="text-[9px] text-[#8C877F]">Invest EUR</label><input type="number" className={inputCls + ' text-[12px] py-1.5'} value={sim.renoInvest || ''} onChange={e => setSim(a.asset_id, 'renoInvest', parseFloat(e.target.value) || 0)} placeholder="50000" /></div>
                        <div><label className="text-[9px] text-[#8C877F]">Rent +%</label><input type="number" className={inputCls + ' text-[12px] py-1.5'} value={sim.renoRentUp} onChange={e => setSim(a.asset_id, 'renoRentUp', parseFloat(e.target.value) || 0)} /></div>
                        <div><label className="text-[9px] text-[#8C877F]">Value +%</label><input type="number" className={inputCls + ' text-[12px] py-1.5'} value={sim.renoValueUp} onChange={e => setSim(a.asset_id, 'renoValueUp', parseFloat(e.target.value) || 0)} /></div>
                      </div>
                      {a.renoPayback > 0 && <p className="text-[11px] text-[#2E6F57] font-sans font-medium">Payback: {a.renoPayback.toFixed(1)} years</p>}
                    </div>
                  </div>

                  {/* Sim Results */}
                  <div className="grid grid-cols-3 md:grid-cols-7 gap-4 pt-4 border-t border-[#E2DDD3]">
                    <SimMetric label="IRR" base={a.baseIrr} sim={a.simIrr} suffix="%" />
                    <SimMetric label="LTV" base={a.baseLtv} sim={a.simLtv} suffix="%" invert />
                    <SimMetric label="DSCR" base={a.baseDscr} sim={a.simDscr} />
                    <SimMetric label="Debt Yield" base={0} sim={a.simDy} suffix="%" />
                    <SimMetric label="Net Yield" base={0} sim={a.simNetYield} suffix="%" />
                    <SimMetric label="Cashflow/yr" base={a.baseNcf} sim={a.simNcf} />
                    <SimMetric label="CoC Return" base={0} sim={a.simCoc} suffix="%" />
                  </div>

                  {/* Negotiation Range */}
                  <div className="bg-[#F4F1EB]/50 rounded-xl p-4">
                    <p className="text-[12px] text-[#5A5A5A] font-sans font-medium mb-3">Optimal Offer Range</p>
                    <div className="grid grid-cols-4 gap-4">
                      {[{ l: 'Conservative', v: fmt(a.conservOffer), c: '#2E6F57' }, { l: 'Target IRR 10%', v: fmt(a.targetOffer), c: '#1F3A34' }, { l: 'Aggressive', v: fmt(a.aggressiveOffer), c: '#A67C52' }, { l: 'Walk-Away', v: fmt(a.walkAway), c: '#8A3C3C' }].map(k => (
                        <div key={k.l} className="text-center"><p className="text-[10px] text-[#8C877F]">{k.l}</p><p className="text-[15px] font-sans font-semibold" style={{ color: k.c }}>{k.v}</p></div>
                      ))}
                    </div>
                  </div>

                  {/* Reset */}
                  {hasChanges && <button onClick={() => setSims(prev => ({ ...prev, [a.asset_id]: { priceDelta: 0, rentDelta: 0, renoInvest: 0, renoRentUp: 5, renoValueUp: 3 } }))} className="text-[11px] text-[#8C877F] hover:underline font-sans">Reset simulation</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
